import React from "react";
import { MapPin, ArrowRight, Bell, Mic, Briefcase, Users2, Coffee, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { eventInfo, themes, formats, beliefs } from "../mock";
import Reveal from "../components/Reveal";

const formatIcons = [Mic, Briefcase, Coffee, Users2];

const Programme = () => {
  return (
    <main>
      <section className="eco-grad-hero pt-[88px] sm:pt-[100px] pb-16 text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#ff243d]/15 blur-[140px] aurora-pulse" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#143DE5]/15 blur-[140px] aurora-pulse" style={{ animationDelay: "2s" }} />
        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10 relative">
          <Reveal>
            <div className="text-sm tracking-[0.2em] uppercase text-white/60 mb-3">Themes &amp; Format</div>
            <h1 className="font-display text-[2.25rem] sm:text-5xl md:text-6xl font-semibold leading-tight max-w-3xl">One day. Six themes. Four formats.</h1>
            <div className="mt-8 flex flex-wrap gap-6 text-white/80 text-sm">
              <a href={eventInfo.mapUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white">
                <MapPin className="w-4 h-4 text-[#ff243d]" />
                <span className="link-underline">{eventInfo.venue}, {eventInfo.city}</span>
                <ExternalLink className="w-3 h-3 opacity-70" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-black">
              <span className="section-line" />Six key themes
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl">The defining questions for Asia's AI ecosystem over the next 12 months</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {themes.map((t, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="rounded-xl border border-slate-200 p-7 hover:shadow-lg transition-all bg-white shine h-full relative overflow-hidden">
                  <div className="absolute -top-px left-0 h-1 w-24 rounded-r-full" style={{ background: t.color }} />
                  <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: t.color }}>Theme 0{i + 1}</div>
                  <h3 className="font-display text-xl text-black font-semibold mt-2 leading-snug">{t.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{t.summary}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-14 md:py-20">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-black">
              <span className="section-line" />Four formats
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl">No slides. No prepared remarks. Moderated conversations.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {formats.map((f, i) => {
              const Icon = formatIcons[i] || Briefcase;
              return (
                <Reveal key={f.name} delay={i * 90}>
                  <div className="rounded-xl bg-white border border-slate-200 p-8 hover:border-[#ff243d] hover:shadow-md transition-all shine h-full">
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-full bg-black text-[#ff243d] flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-display text-2xl font-semibold text-black">{f.name}</h3>
                        <div className="text-xs tracking-[0.2em] uppercase text-[#ff243d] mt-1">{f.style}</div>
                        <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.body}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-14 md:py-20">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-black">
              <span className="section-line" />Editorial principles
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {beliefs.map((b, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="rounded-xl border border-slate-200 p-7 bg-white shine h-full relative overflow-hidden">
                  <div className="absolute -top-px left-0 h-1 w-24 rounded-r-full" style={{ background: b.accent }} />
                  <div className="font-display text-5xl leading-none opacity-25" style={{ color: b.accent }}>0{i + 1}</div>
                  <h3 className="font-display text-xl font-semibold text-black mt-3">{b.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="eco-bg-deep text-white py-14 md:py-20 relative overflow-hidden">
        <div className="absolute -top-32 right-0 w-[420px] h-[420px] rounded-full bg-[#ff243d]/15 blur-[140px] aurora-pulse" />
        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10 flex flex-col md:flex-row gap-8 items-center justify-between relative">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold">Agenda to Be Announced</h3>
            <p className="mt-2 text-white/70">Get the final agenda, speaker list and partnership information delivered to your inbox.</p>
          </div>
          <Link to="/registration">
            <Button className="bg-[#ff243d] hover:bg-[#e01a32] rounded-full h-12 px-7"><Bell className="w-4 h-4 mr-2" />Register Interest <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Programme;
